// AIDialerScreenPop — global slide-in drawer that fires when the AI Dialer's
// hand-off engine emits `ai-dialer:screen-pop` for this agent.
//
// Lifecycle:
//   1. Socket.IO event arrives with { callId, lead, qualifyingAnswers, timeoutSec }.
//   2. Drawer slides in from the right. Countdown ticks down to 0.
//   3. Agent clicks Accept → POST /calls/:id/accept-transfer, then join
//      Twilio conference via @twilio/voice-sdk.
//   4. After accept, drawer shrinks into a top-of-page pill with mute / hangup.
//   5. Agent clicks Decline (or countdown hits 0) → POST decline-transfer.
//
// Mounted in Layout.jsx so it works on any page.

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { PhoneIncoming, PhoneOff, X, MapPin, ClipboardList, MicOff, Mic, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import { getSocket } from '../../lib/socket.js';
import { apiPost } from '../../lib/api.js';
import { joinAIDialerConference, mute as muteCall, hangup as hangupCall } from '../../lib/aiDialerVoice.js';
import { useAIDialerEnabled } from '../../lib/aiDialerFlag.js';

function fmtPhone(p) {
  if (!p) return '';
  const d = String(p).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p;
}

export default function AIDialerScreenPop() {
  const [pop, setPop] = useState(null);         // active screen-pop payload
  const [countdown, setCountdown] = useState(0);
  const [active, setActive] = useState(null);   // { callId, lead, muted }
  const timerRef = useRef(null);

  // Don't even attach a listener if the flag is off — saves an idle socket subscription.
  // Uses the runtime per-tenant flag (falls back to the build flag).
  const { enabled: flagOn } = useAIDialerEnabled();

  const clearTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const dismiss = useCallback(() => {
    setPop(null);
    setCountdown(0);
    clearTimer();
  }, []);

  const decline = useCallback(async (silent = false) => {
    if (!pop?.callId) return dismiss();
    try {
      await apiPost(`/dialer/v1/calls/${pop.callId}/decline-transfer`, {});
      if (!silent) toast('Transfer declined', { icon: '👋' });
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.error || 'Decline failed');
    }
    dismiss();
  }, [pop, dismiss]);

  const accept = useCallback(async () => {
    if (!pop?.callId) return;
    try {
      const r = await apiPost(`/dialer/v1/calls/${pop.callId}/accept-transfer`, {});
      const conf = r?.data?.conferenceSid || pop.conferenceSid;
      try {
        await joinAIDialerConference({ callId: pop.callId, conferenceSid: conf });
      } catch (vErr) {
        // The accept is committed even if the browser SDK leg fails. The
        // server-side hand-off engine still falls back to the cell forward
        // if the conference join doesn't materialize.
        toast.error('Could not connect browser audio — check Twilio Voice SDK config. Cell forward will still ring.');
        console.error('[ScreenPop] joinAIDialerConference failed', vErr);
      }
      setActive({ callId: pop.callId, lead: pop.lead, muted: false });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Accept failed');
    }
    dismiss();
  }, [pop, dismiss]);

  // Countdown timer when a pop is active.
  useEffect(() => {
    if (!pop) return undefined;
    clearTimer();
    setCountdown(pop.timeoutSec || 25);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearTimer();
          // Auto-decline at timeout (silent — agent walked away).
          decline(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return clearTimer;
  }, [pop, decline]);

  // Socket subscription.
  useEffect(() => {
    if (!flagOn) return undefined;
    const socket = getSocket();
    if (!socket?.on) return undefined;

    const onPop = (payload) => {
      if (!payload || !payload.callId) return;
      // Replace any existing pop (only one transfer can be live per agent).
      setPop(payload);
    };

    socket.on('ai-dialer:screen-pop', onPop);
    return () => { socket.off?.('ai-dialer:screen-pop', onPop); };
  }, [flagOn]);

  const handleHangup = () => {
    hangupCall();
    setActive(null);
  };
  const toggleMute = () => {
    setActive((a) => {
      if (!a) return a;
      const next = !a.muted;
      muteCall(next);
      return { ...a, muted: next };
    });
  };

  if (!flagOn) return null;

  // Active-call pill (after Accept).
  if (active) {
    return (
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-60 flex items-center gap-3 panel px-4 py-2 shadow-glow-purple border border-civix-purple/40">
        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
        <Phone className="w-4 h-4 text-white/70" />
        <div className="text-sm font-medium text-white">
          {active.lead?.firstName || ''} {active.lead?.lastName || ''} — live
        </div>
        <button
          onClick={toggleMute}
          aria-label={active.muted ? 'Unmute microphone' : 'Mute microphone'}
          aria-pressed={active.muted}
          className={`p-1.5 rounded-lg ${active.muted ? 'bg-yellow-500/20 text-yellow-300' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}
          title={active.muted ? 'Unmute' : 'Mute'}
        >
          {active.muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
        <button
          onClick={handleHangup}
          aria-label="Hang up call"
          className="p-1.5 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30"
          title="Hang up"
        >
          <PhoneOff className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (!pop) return null;

  const qa = pop.qualifyingAnswers || {};
  const qaEntries = Object.entries(qa).filter(([, v]) => v != null && v !== '');

  return (
    <div className="fixed inset-0 z-55 pointer-events-none">
      <div className="absolute right-4 top-4 w-[380px] max-w-[calc(100vw-2rem)] pointer-events-auto">
        <div className="panel border border-civix-purple/40 shadow-glow-purple rounded-2xl overflow-hidden animate-in slide-in-from-right">
          <div className="bg-civix-purple/20 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-green-400/30 flex items-center justify-center">
              <PhoneIncoming className="w-5 h-5 text-green-300 animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-civix-cyan font-semibold">AI Dialer transfer</div>
              <div className="text-sm font-bold text-white truncate">
                {(pop.lead?.firstName || '') + ' ' + (pop.lead?.lastName || '') || 'Qualified lead'}
              </div>
            </div>
            <button
              onClick={() => decline(false)}
              aria-label="Decline transfer"
              className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/10"
              title="Decline"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-4 py-3 space-y-3">
            <div className="flex items-center gap-3 text-xs text-white/70">
              {pop.lead?.state && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {pop.lead.state}
                </span>
              )}
              {pop.lead?.phone && <span>{fmtPhone(pop.lead.phone)}</span>}
            </div>

            {qaEntries.length > 0 && (
              <div className="rounded-lg bg-white/5 p-3 text-xs space-y-1.5">
                <div className="flex items-center gap-1.5 text-white/60 uppercase tracking-wider mb-1">
                  <ClipboardList className="w-3 h-3" /> Qualifying answers
                </div>
                {qaEntries.slice(0, 6).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="text-white/50 capitalize shrink-0">{k.replace(/([A-Z])/g, ' $1')}:</span>
                    <span className="text-white/90 truncate">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}

            {pop.summary && (
              <div className="rounded-lg bg-white/5 p-3 text-xs">
                <div className="text-white/60 uppercase tracking-wider mb-1">AI summary</div>
                <div className="text-white/80">{pop.summary}</div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={accept}
                className="flex-1 btn-primary flex items-center justify-center gap-2 py-2.5"
                data-testid="screen-pop-accept"
              >
                <PhoneIncoming className="w-4 h-4" />
                Accept ({countdown}s)
              </button>
              <button
                onClick={() => decline(false)}
                className="px-4 py-2.5 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 text-sm font-medium"
                data-testid="screen-pop-decline"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
